import SectionHeading from '../shared/SectionHeading';
import { SPACING, TEXT_STYLES } from '../../constants/styles';

function ProjectFinalDesign({ title = 'Final Design', description, images = [], content = [] }) {
  if (!images || images.length === 0) return null;

  return (
    <section id="final-design" className="w-screen relative mt-8 md:mt-12 scroll-mt-32 mb-0" style={{ marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)' }}>
      <div className={`max-w-5xl mx-auto ${SPACING.container} pt-12 md:pt-16`}>
        {/* Final Design Header */}
        <SectionHeading className="mb-8 text-center">{title}</SectionHeading>
        
        {/* Description */}
        {description && (
          <div className={`${TEXT_STYLES.body} mb-12 max-w-3xl mx-auto`}>
            {description.split('\n\n').map((paragraph, index) => {
              // Check if this is a list item (starts with •)
              if (paragraph.startsWith('•')) {
                return (
                  <p key={index} className={index > 0 ? 'mt-4' : ''}>
                    {paragraph}
                  </p>
                );
              }
              // Regular paragraph
              return (
                <p key={index} className={index > 0 ? 'mt-6' : ''}>
                  {paragraph}
                </p>
              );
            })}
          </div>
        )}
        
        {/* Final designs in rows */}
        {images.length === 1 ? (
          // Single image with optional content
          content ? (
            // Arc: Image on left, content on right
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 items-center">
              <div className="w-full md:col-span-3">
                <img
                  src={images[0]}
                  alt="Final Design"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="w-full md:col-span-1 space-y-8">
                {content.filter(item => !item.imageRight).map((item, index) => (
                  <div key={index}>
                    <h3 className="text-lg md:text-xl font-medium text-[#0D0D0D] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-base text-[#666666] font-normal leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Single image only
            <div className="w-full">
              <img
                src={images[0]}
                alt="Final Design"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          )
        ) : images.length >= 2 && content ? (
          // Arc: Multiple images with content
          <div className="space-y-16">
            {/* First image: Tasks - Image left, content right */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 items-center">
              <div className="w-full md:col-span-3">
                <img
                  src={images[0]}
                  alt="Final Design"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              <div className="w-full md:col-span-1 space-y-8">
                {content.filter(item => !item.imageRight).map((item, index) => (
                  <div key={index}>
                    <h3 className="text-lg md:text-xl font-medium text-[#0D0D0D] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-base text-[#666666] font-normal leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {/* Second image: Profiles - Content left, image right */}
            {content.filter(item => item.imageRight).map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 items-center">
                <div className="w-full md:col-span-1">
                  <h3 className="text-lg md:text-xl font-medium text-[#0D0D0D] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-base text-[#666666] font-normal leading-relaxed">
                    {item.description}
                  </p>
                </div>
                <div className="w-full md:col-span-3">
                  <img
                    src={images[1]}
                    alt="Final Design"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              </div>
            ))}
            {/* Third image: UI - Full width */}
            {images.length >= 3 && (
              <div className="w-full">
                {typeof images[2] === 'object' && images[2].src ? (
                  <>
                    <img
                      src={images[2].src}
                      alt={images[2].caption || 'Final Design'}
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                    {images[2].caption && (
                      <div className="mt-4 flex items-center justify-center">
                        <div className="inline-flex items-center gap-2 bg-[#F5F5F5] rounded-[100px] px-4 md:px-6 py-1.5 border border-[#E5E5E5]">
                          <span className="material-symbols-outlined text-sm text-[#666666] animate-bounce-arrow">
                            expand_less
                          </span>
                          <span className="text-xs text-[#666666] font-normal">
                            {images[2].caption}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <img
                    src={images[2]}
                    alt="Final Design"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                )}
              </div>
            )}
            {/* Fourth image: Promotional Post - Full width with caption */}
            {images.length >= 4 && (
              <div className="w-full">
                {typeof images[3] === 'object' && images[3].src ? (
                  <>
                    <img
                      src={images[3].src}
                      alt={images[3].caption || 'Final Design'}
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                    {images[3].caption && (
                      <div className="mt-4 flex items-center justify-center">
                        <div className="inline-flex items-center gap-2 bg-[#F5F5F5] rounded-[100px] px-4 md:px-6 py-1.5 border border-[#E5E5E5]">
                          <span className="material-symbols-outlined text-sm text-[#666666] animate-bounce-arrow">
                            expand_less
                          </span>
                          <span className="text-xs text-[#666666] font-normal">
                            {images[3].caption}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <img
                    src={images[3]}
                    alt="Final Design"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                )}
              </div>
            )}
          </div>
        ) : (
          // Multiple images without special content
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {images.map((image, index) => (
              <div key={index} className="w-full">
                <img
                  src={image}
                  alt={`Final Design ${index + 1}`}
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default ProjectFinalDesign;
